/**
 * The real terrain, as a small tile beside the schematic board.
 *
 * The card's board is a diagram: eight tiles by three, forces stood on it in
 * their own colours. It says who is in the fight and says nothing at all about
 * the ground, which is half of what decides one. This draws the ground.
 *
 * Not a replacement for that diagram and not a map to play from — an inset, at
 * a size where only the shapes survive: where the water is, where the cover is,
 * where the roads run, where the ground breaks. Everything else in a `.board`
 * file is dropped by `scripts/boards.py` before it ever reaches the browser.
 *
 * One hue, value carrying a fixed importance order:
 *
 *     water -> low ground -> high ground -> buildings -> woods -> roads
 *     darkest ------------------------------------------------> lightest
 *
 * The order is by what is rare rather than by what is important, because a
 * board made mostly of one thing has to read as ground with markings on it and
 * not as a block. Archipelago is half water and Downtown Arbitration is most
 * of the way to solid buildings; both were coming out as a bright slab with
 * the land drawn on top. Water is a hole in the ground and a city block is a
 * thing standing on it, so both go dark and the streets between them light -
 * which is what a city looks like from above anyway.
 *
 * so it survives being 200 pixels wide, and survives colour blindness. The two
 * darkest values are the card's own board colours, so the tile sits in the card
 * rather than on it.
 */

import boardsJson from "../content/boards.json";

/** One board, as `scripts/boards.py` encodes it. */
type Board = {
  w: number;
  h: number;
  /** One character a hex: `.` open, `w`/`W` woods, `~` water, `=` road, `#` building. */
  t: string;
  /** One character a hex: the level, `a`..`s` for -9..9. */
  l: string;
  /** Exit mask per road hex that declares one, keyed by hex index. */
  r: Record<string, string>;
};

const BOARDS: Record<string, Board> = boardsJson.boards;

/** Level characters decode to a signed level. */
const LEVELS = "abcdefghijklmnopqrs";

const INK = {
  ground: "#0b1017",
  low: "#0f1622",
  grid: "#172134",
  high: "#22314b",
  woods: "#2f5f86",
  water: "#1e4468",
  edge: "#63a6de",
  road: "#8fb6d9",
  building: "#3d4d69",
};

/**
 * Draw the boards a scenario names, laid out `cols` across, onto a canvas.
 *
 * Returns false when the tile cannot be drawn — a board the index named but
 * `boards.json` has never heard of — so the caller can leave the inset out
 * rather than draw an empty box.
 */
export type ZoneMark = {
  /** The zone name, MegaMek's own spelling: `N`, `SW`, `CTR`. */
  start: string;
  /** The colour this force fights in, as CSS. */
  colour: string;
};

export function drawMinimap(
  canvas: HTMLCanvasElement,
  names: readonly string[],
  cols: number,
  zones: readonly ZoneMark[] = [],
): boolean {
  const sheets = names.map((n) => BOARDS[n]).filter((b): b is Board => !!b);
  if (sheets.length === 0 || sheets.length !== names.length) return false;

  const across = Math.max(1, cols);
  const down = Math.ceil(sheets.length / across);
  const sheetW = sheets[0]!.w;
  const sheetH = sheets[0]!.h;
  const hexes = { x: sheetW * across, y: sheetH * down };

  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  // A hex is drawn as a flat-topped cell on the same lattice the board uses:
  // columns step three quarters of a width and every other one drops half a
  // height. The tile is sized to fill the canvas it was given.
  const radius = Math.min(
    canvas.width / (hexes.x * 1.5 + 0.5),
    canvas.height / (hexes.y * Math.sqrt(3) + Math.sqrt(3) / 2),
  );
  const halfH = radius * (Math.sqrt(3) / 2);

  ctx.fillStyle = INK.ground;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const at = (sheet: number, x: number, y: number) => {
    const ox = (sheet % across) * sheetW;
    const oy = Math.floor(sheet / across) * sheetH;
    return {
      cx: (ox + x) * radius * 1.5 + radius,
      cy: (oy + y) * halfH * 2 + halfH * ((ox + x) % 2 === 1 ? 2 : 1),
    };
  };

  const hex = (cx: number, cy: number, fill: string) => {
    ctx.beginPath();
    for (let v = 0; v < 6; v++) {
      const a = (Math.PI / 3) * v;
      const px = cx + radius * Math.cos(a);
      const py = cy + halfH * Math.sin(a) * (2 / Math.sqrt(3));
      v === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
  };

  // Ground first, banded against the board's own median so a board that sits
  // entirely at level 2 reads as flat rather than as a plateau. Three bands
  // and not two: Sinkhole's one feature is a hole, and against a high/flat
  // pair a hole is indistinguishable from open ground.
  const allLevels = sheets.flatMap((s) =>
    [...s.l].map((c) => LEVELS.indexOf(c) - 9),
  );
  const median =
    allLevels.slice().sort((a, b) => a - b)[Math.floor(allLevels.length / 2)] ??
    0;

  // Clipped, because the edge rows are deliberately overdrawn. A flat-top
  // lattice staggers every other column half a hex down, so the top and bottom
  // of the field come out as a sawtooth with bare ground in the notches - which
  // reads as a band welded to the frame rather than as terrain. Drawing one row
  // beyond each end and cutting it off gives a straight edge.
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, canvas.width, canvas.height);
  ctx.clip();

  for (const [i, sheet] of sheets.entries()) {
    for (let y = 0; y < sheet.h; y++) {
      for (let x = 0; x < sheet.w; x++) {
        const n = y * sheet.w + x;
        const kind = sheet.t[n]!;
        const level = LEVELS.indexOf(sheet.l[n]!) - 9;
        const { cx, cy } = at(i, x, y);
        let fill =
          level > median ? INK.high : level < median ? INK.low : INK.grid;
        if (kind === "w" || kind === "W") fill = INK.woods;
        else if (kind === "~") fill = INK.water;
        else if (kind === "=") fill = INK.road;
        else if (kind === "#") fill = INK.building;
        hex(cx, cy, fill);
        // The notch above the first row and below the last is filled by
        // repeating that hex, not by inventing a neighbour: the tile is a
        // summary, and the alternative is a hole.
        if (y === 0) hex(cx, cy - halfH * 2, fill);
        if (y === sheet.h - 1) hex(cx, cy + halfH * 2, fill);
        // And the same for the two ends of a row. A flat-top hex comes to a
        // point left and right, so the first and last columns scallop the way
        // the first and last rows sawtooth. Two repeats cover the notch
        // whichever way the neighbouring column steps.
        if (x === 0 && i % across === 0) {
          hex(cx - radius * 1.5, cy - halfH, fill);
          hex(cx - radius * 1.5, cy + halfH, fill);
        }
        if (x === sheet.w - 1 && i % across === across - 1) {
          hex(cx + radius * 1.5, cy - halfH, fill);
          hex(cx + radius * 1.5, cy + halfH, fill);
        }
      }
    }
  }
  ctx.restore();
  // The deployment zones, over the terrain, in the colours the forces fight
  // in on the board beside this. Outlines rather than washes: the ground is
  // what the tile is for, and a filled zone would hide the thing it is
  // annotating.
  for (const zone of zones) {
    const box = zoneRect(zone.start);
    if (!box) continue;
    const line = Math.max(1, Math.round(canvas.width / 220));
    // Inset by the whole line width. A zone that reaches the board's edge -
    // and most do, that is what an edge deployment is - would otherwise be
    // stroked centred on the canvas boundary, so half the line falls outside
    // and the rest reads as a hard band welded to the frame.
    const x = Math.max(line, box.x0 * canvas.width + line / 2);
    const y = Math.max(line, box.y0 * canvas.height + line / 2);
    const right = Math.min(
      canvas.width - line,
      box.x1 * canvas.width - line / 2,
    );
    const bottom = Math.min(
      canvas.height - line,
      box.y1 * canvas.height - line / 2,
    );
    const w = right - x;
    const h = bottom - y;
    if (w <= 0 || h <= 0) continue;
    ctx.save();
    ctx.strokeStyle = zone.colour;
    ctx.lineWidth = line;
    ctx.globalAlpha = 0.85;
    ctx.strokeRect(x, y, w, h);
    // A wash faint enough to read as "this end is theirs" and not as terrain.
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = zone.colour;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
  }
  return true;
}

/**
 * The part of the board a deployment zone covers, as fractions.
 *
 * MegaMek's own zones are deeper than this - `N` is the top three rows of a
 * seventeen-row board, not the top third - but the tile is a summary and an
 * outline three pixels tall would read as a line rather than an area. These
 * are the same bands the card's own layout uses, so the two pictures agree.
 */
function zoneRect(
  start: string,
): { x0: number; x1: number; y0: number; y1: number } | null {
  const zone = (start ?? "").toUpperCase().replace(" (DEEP)", "");
  if (!zone || zone === "ANY") return null;
  const x0 = zone.includes("W") ? 0 : zone.includes("E") ? 0.5 : 0;
  const x1 = zone.includes("W") ? 0.5 : zone.includes("E") ? 1 : 1;
  if (zone.startsWith("N")) return { x0, x1, y0: 0, y1: 0.28 };
  if (zone.startsWith("S")) return { x0, x1, y0: 0.72, y1: 1 };
  if (zone.startsWith("CTR")) return { x0: 0.3, x1: 0.7, y0: 0.36, y1: 0.64 };
  // A bare E or W owns its whole side.
  return { x0, x1, y0: 0, y1: 1 };
}

/**
 * The exact pixel size a set of boards wants, for a given width.
 *
 * A hex lattice has one true aspect ratio and it is not the board's own: a
 * column steps three quarters of a hex across, so 32 hexes span 24.5 widths,
 * not 32. Sizing the canvas by the board's 16:17 instead left the lattice
 * fitting itself to whichever axis ran out first and leaving the other short -
 * about a sixth of the width, on a two-sheet map. Bare ground is only half the
 * cost of that: the deployment zones are placed against the canvas, so they
 * drifted off the terrain they were meant to be marking.
 */
export function minimapSize(
  names: readonly string[],
  cols: number,
  width: number,
): { width: number; height: number } {
  const sheets = names.map((n) => BOARDS[n]).filter((b): b is Board => !!b);
  if (sheets.length === 0) return { width, height: width };
  const across = Math.max(1, cols);
  const down = Math.ceil(sheets.length / across);
  const x = sheets[0]!.w * across;
  const y = sheets[0]!.h * down;
  // In radius units: the lattice is this wide and this tall.
  const spanW = x * 1.5 + 0.5;
  const spanH = (y + 0.5) * Math.sqrt(3);
  return { width, height: Math.round((width * spanH) / spanW) };
}

/** Whether a tile can be drawn for these boards at all. */
export function haveBoards(names: readonly string[]): boolean {
  return names.length > 0 && names.every((n) => !!BOARDS[n]);
}
